import { Link } from 'react-router-dom'
import { Button } from '../ui/button'
import { ArrowRight, MessageSquare, Star, Quote } from 'lucide-react'
import { useScrollAnim } from '../../hooks/use-scroll-anim'
import { useI18n } from '../../i18n'

interface CTASectionProps {
  isLoggedIn: boolean
  onStart: () => void
}

const reviews = [
  { name: '张经理', company: '某连锁餐饮集团', text: '上线一周，门店咨询量下降60%，员工再也不用反复打电话问总部了', stars: 5 },
  { name: '李总监', company: '某金融科技公司', text: '合规查询从平均2小时缩短到10秒，这个ROI太惊人了', stars: 5 },
  { name: '王校长', company: '某教育培训机构', text: '学生和家长的咨询响应速度大幅提升，满意度直接拉满', stars: 5 },
  { name: '陈厂长', company: '某制造企业', text: '工艺规程查询不再依赖老员工，新人也能快速上手操作', stars: 5 },
  { name: '赵店长', company: '某新零售品牌', text: '商品知识问答准确率极高，导购培训成本降低了70%', stars: 5 },
]

const reviewsEn = [
  { name: 'Zhang M.', company: 'Chain Restaurant Group', text: 'Within a week, store inquiries dropped 60%. Employees no longer call HQ repeatedly.', stars: 5 },
  { name: 'Li D.', company: 'FinTech Corp', text: 'Compliance queries went from 2 hours to 10 seconds. The ROI is astonishing.', stars: 5 },
  { name: 'Wang X.', company: 'Education Institute', text: 'Student and parent response times improved dramatically. Satisfaction is through the roof.', stars: 5 },
  { name: 'Chen C.', company: 'Manufacturing Co.', text: 'SOP queries no longer depend on veteran staff. New hires get up to speed fast.', stars: 5 },
  { name: 'Zhao S.', company: 'New Retail Brand', text: 'Product knowledge Q&A accuracy is outstanding. Training costs dropped 70%.', stars: 5 },
]

export function CTASection({ isLoggedIn, onStart }: CTASectionProps) {
  const { ref, isVisible } = useScrollAnim()
  const { lang, t } = useI18n()
  const data = lang === 'zh' ? reviews : reviewsEn

  return (
    <section className="py-24 min-h-screen flex items-center relative overflow-hidden" style={{ scrollSnapAlign: 'start' }}>
      {/* Faded review cards scattered in background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {data.map((review, i) => {
          const positions = [
            { top: '8%', left: '5%', rotate: '-3deg' },
            { top: '15%', right: '8%', rotate: '2deg' },
            { bottom: '20%', left: '10%', rotate: '1deg' },
            { bottom: '12%', right: '5%', rotate: '-2deg' },
            { top: '45%', left: '2%', rotate: '-1deg' },
          ]
          const pos = positions[i]
          return (
            <div
              key={i}
              className="absolute max-w-[220px] rounded-xl bg-white/30 backdrop-blur-sm border border-white/40 p-3 shadow-sm"
              style={{
                ...pos,
                opacity: isVisible ? 0.35 : 0,
                transform: `rotate(${pos.rotate})`,
                transition: `opacity 1.5s ease ${i * 300}ms`,
              }}
            >
              <div className="flex gap-0.5 mb-1">
                {Array.from({ length: review.stars }).map((_, j) => (
                  <Star key={j} className="h-2.5 w-2.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-[10px] text-gray-500 leading-relaxed line-clamp-2">{review.text}</p>
            </div>
          )
        })}
      </div>

      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          ref={ref}
          className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="text-2xl md:text-4xl font-semibold text-[#1a1a1a] mb-4 tracking-tight">
            {t('让AI管家为您的企业效劳', 'Let an AI Butler Serve Your Enterprise')}
          </h2>
          <p className="text-base text-gray-400 max-w-md mx-auto mb-10">
            {t('免费注册即享10万Token额度，即刻体验企业专属AI管家', 'Sign up free for 100K tokens. Experience your dedicated AI Butler instantly')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="bg-[#1a1a1a] text-white hover:bg-[#333] px-8 h-12 text-sm font-medium rounded-xl shadow-md" onClick={onStart}>
              {isLoggedIn ? t('进入工作台', 'Dashboard') : t('免费开始使用', 'Get Started Free')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" className="border-black/[0.08] bg-white/70 backdrop-blur-sm text-gray-600 hover:bg-white hover:text-gray-900 px-8 h-12 text-sm font-medium rounded-xl">
              <MessageSquare className="mr-2 h-4 w-4" />
              {t('联系我们', 'Contact Us')}
            </Button>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700 px-8 h-12 text-sm font-medium rounded-xl">
                {t('查看定价', 'View Pricing')}
              </Button>
            </Link>
          </div>

          {/* Inline reviews */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.slice(0, 3).map((review, i) => (
              <div
                key={i}
                className={`rounded-2xl bg-white/50 backdrop-blur-sm border border-white/60 p-5 text-left transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: isVisible ? `${(i + 2) * 200}ms` : '0ms' }}
              >
                <Quote className="h-4 w-4 text-orange-300 mb-2" />
                <p className="text-xs text-gray-600 leading-relaxed mb-3">{review.text}</p>
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-orange-100 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-orange-500">{review.name[0]}</span>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-[#1a1a1a]">{review.name}</div>
                    <div className="text-[10px] text-gray-400">{review.company}</div>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {Array.from({ length: review.stars }).map((_, j) => (
                      <Star key={j} className="h-2.5 w-2.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
